/** Per-browser conveniences only (rail pinned, assistant open, theme, hide off duty). Nothing here is state of record. */
import { useSyncExternalStore } from 'react';

export interface Prefs {
  railPinned: boolean;
  assistantOpen: boolean;
  theme: 'system' | 'dark' | 'light';
  /** The rail's Team list shows only people on duty (and you). */
  hideOffDuty: boolean;
}

const KEY = 'lsc.prefs';
const DEFAULTS: Prefs = { railPinned: false, assistantOpen: true, theme: 'system', hideOffDuty: false };
const listeners = new Set<() => void>();
let cached: Prefs = load();

function load(): Prefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<Prefs>;
    return { ...DEFAULTS, ...parsed };
  } catch {
    return DEFAULTS;
  }
}

export function setPrefs(patch: Partial<Prefs>): void {
  cached = { ...cached, ...patch };
  try {
    localStorage.setItem(KEY, JSON.stringify(cached));
  } catch {
    /* private mode */
  }
  applyTheme(cached.theme);
  for (const fn of listeners) fn();
}

export function usePrefs(): Prefs {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => cached,
    () => DEFAULTS,
  );
}

export function applyTheme(theme: Prefs['theme']): void {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
}

applyTheme(cached.theme);
